import { Mass } from "./helpers/Mass";
import { Money } from "./helpers/Money";
import {
  BundledItems,
  DroppsDiscountFormula,
  ShopifyMedia,
  ShopifyProduct,
  PdpIngredientSection,
  PdpTabData,
  Badge,
} from "@/store";

export type LiquidProduct = {
  id: number;
  title: string;
  handle: string;
  description: string;
  published_at: string;
  created_at: string;
  vendor: string;
  type: string;
  tags: string[];
  price: number;
  price_min: number;
  price_max: number;
  available: boolean;
  price_varies: boolean;
  compare_at_price: number | null;
  compare_at_price_min: number;
  compare_at_price_max: number;
  compare_at_price_varies: boolean;
  variants: LiquidVariant[];
  images: string[];
  featured_image: string | null;
  options: string[];
  media: LiquidMedia[];
  requires_selling_plan: boolean;
  selling_plan_groups: {
    id: string;
    name: string;
    options: {
      name: string;
      position: number;
      values: string[];
    }[];
    selling_plans: LiquidSellingPlan[];
    app_id: string;
  }[];
  content: string;
  collections: Array<{
    id: string;
    handle: string;
    title: string;
  }>;
};

type LiquidVariant = {
  id: number;
  title: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  sku: string;
  requires_shipping: boolean;
  taxable: boolean;
  featured_image: LiquidMedia | null; // todo
  available: boolean;
  name: string;
  public_title: string;
  options: string[];
  price: number;
  weight: number;
  compare_at_price: number | null;
  inventory_quantity: number;
  inventory_management: string;
  inventory_policy: string;
  barcode: string;
  requires_selling_plan: boolean;
  selling_plan_allocations: {
    price_adjustments: {
      position: number;
      price: number;
    }[];
    price: number;
    compare_at_price: number;
    per_delivery_price: number;
    selling_plan_id: number;
    selling_plan_group_id: string;
  }[];

  quantity_rule: {
    min: number;
    max: null;
    increment: number;
  };

  package_quantity: number;
  variant_multiplier: number;
  quantity_discount: number;
};

type LiquidMedia = {
  alt: string | null;
  id: number;
  position: number;
  preview_image: {
    alt?: string | null;
    aspect_ratio: number;
    height: number;
    width: number;
    src: string;
  };
  aspect_ratio: number;
  media_type: string;
  height?: number;
  src?: string;
  width?: number;
  duration?: number;
  sources?: {
    format: string;
    height: number;
    mime_type: string;
    url: string;
    width: number;
  }[];
};

export type LiquidSellingPlanPriceAdjustment = {
  order_count: number | null;
  position: number;
  value_type: string;
  value: number;
};

type LiquidSellingPlan = {
  id: number;
  name: string;
  description: string | null;
  options: {
    name: string;
    position: number;
    value: string;
  }[];
  recurring_deliveries: boolean;
  price_adjustments: LiquidSellingPlanPriceAdjustment[];
  checkout_charge: {
    value_type: string;
    value: number;
  };
};

function mapMedia(media: LiquidMedia): ShopifyMedia {
  if (media.media_type === "video") {
    return {
      ...media.preview_image,
      alt: media.alt,
      url: media.preview_image.src,
      mediaType: "video",
      sources: (media.sources ?? []).map(({ mime_type, url, ...rest }) => ({
        ...rest,
        mimeType: mime_type,
        videoUrl: url,
      })),
    };
  } else {
    return {
      ...media.preview_image,
      alt: media.alt,
      url: media.preview_image.src,
      mediaType: "image",
    };
  }
}

function mapDiscount(
  x: LiquidSellingPlanPriceAdjustment
): DroppsDiscountFormula {
  switch (x.value_type) {
    case "fixed_amount": {
      const offset = Money.from(x.value, "$");
      return {
        kind: "fixed",
        offset,
      };
    }
    case "price": {
      return {
        kind: "price",
        price: Money.from(x.value, "$"),
      };
    }
    case "percentage": {
      return {
        kind: "percentage",
        percentage: x.value,
      };
    }
    default:
      throw new Error(`unexpected adjustment ${x}`);
  }
}

type LiquidToShopifyProductOptions = {
  bundledItems?: LiquidProduct[];
  hiddenVariants?: Set<number>;
  hidden?: boolean;
  badge?: Badge;
  collections?: Array<{
    id: string;
    handle: string;
    title: string;
  }>;
  frontBoxImage?: string;
  hideSubscription?: boolean;
  overrideFutureShipments?: string;
  pdpIngredientSection?: PdpIngredientSection | undefined;
  pdpTabData?: PdpTabData;
  variantMetadata?: any;
  productShortText: string;
};

export function liquidToShopifyProduct(
  { description, ...product }: LiquidProduct,
  options?: LiquidToShopifyProductOptions
): ShopifyProduct {
  let bundledProducts: BundledItems | undefined = undefined;
  if (options?.bundledItems) {
    bundledProducts = {
      bundledSubscriptionProducts: options?.bundledItems?.map((x) => {
        return liquidToShopifyProduct(x);
      }),
    };
  }

  const formattedVariants = product.variants.map((v) => {
    return {
      ...v,
      price: Money.from(v.price, "¢"),
      compare_at_price:
        v.compare_at_price != null ? Money.from(v.compare_at_price, "¢") : null,
      hidden: options?.hiddenVariants?.has(v.id) ?? false,
      weight: Mass.from(v.weight, "g"),
      featured_image: v.featured_image?.src ? v.featured_image.src : null,
    };
  });

  const media = product.media.map(mapMedia);
  const sellingPlanNodes =
    product.selling_plan_groups?.flatMap((x) => x.selling_plans) ?? [];
  const sellingPlans = sellingPlanNodes.map((x) => {
    const adjustment = x.price_adjustments[0];
    const discount = adjustment
      ? mapDiscount(adjustment)
      : ({ kind: "percentage", percentage: 0 } as const);

    return {
      id: x.id,
      name: x.name,
      discount,
    };
  });

  return {
    ...product,
    descriptionHtml: description,
    media,
    hidden: options?.hidden ?? false,
    badge: {...options?.badge},
    collections: options?.collections ?? product.collections,
    frontBoxImage: options?.frontBoxImage ?? "",
    bundledItems: bundledProducts,
    variants: formattedVariants,
    sellingPlans,
    hideSubscription: !!options?.hideSubscription,
    overrideFutureShipments: options?.overrideFutureShipments,
    pdpIngredientSection: options?.pdpIngredientSection,
    pdpTabData: options?.pdpTabData,
    variantMetadata: options?.variantMetadata,
    productShortText: options?.productShortText ?? "",
  };
}
